import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Search, Package, Download, Upload, Plus, Save, ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


/**
 * VendorPicker — searchable combobox over a static vendors array.
 * Admin types store name / personal name / aniyor code; we filter
 * client-side. Calls onChange(vendorId) — 'all' for no filter.
 */
const VendorPicker = ({ vendors = [], value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const resolveName = (v) => (
    v?.identity?.store_name
    || v?.vendor_profile?.business_details?.business_name
    || v?.name
    || v?.email
    || v?.id
  );

  const selectedVendor = value && value !== 'all' ? vendors.find(v => v.id === value) : null;
  const selectedLabel = value === 'all' || !value
    ? `All Vendors (${vendors.length})`
    : selectedVendor
      ? `${resolveName(selectedVendor)}${selectedVendor.aniyor_id ? ` · ${selectedVendor.aniyor_id}` : ''}`
      : 'All Vendors';

  const q = query.trim().toLowerCase();
  const filtered = !q ? vendors : vendors.filter(v => {
    const haystack = [
      resolveName(v),
      v?.aniyor_id,
      v?.name,
      v?.email,
      v?.phone,
      v?.identity?.legal_name,
      v?.vendor_profile?.business_details?.business_name,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex items-center justify-between gap-2 hover:border-gray-400"
        data-testid="admin-inventory-vendor-picker"
      >
        <span className="truncate text-left">{selectedLabel}</span>
        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden">
          <div className="relative border-b p-2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by store, name, or VEN code…"
              className="w-full pl-7 pr-7 py-1.5 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="admin-inventory-vendor-search"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            <button
              onClick={() => { onChange('all'); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${value === 'all' ? 'bg-blue-50 text-blue-900 font-medium' : ''}`}
              data-testid="vendor-option-all"
            >
              All Vendors ({vendors.length})
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No vendors match "{query}"</div>
            ) : filtered.map(v => {
              const name = resolveName(v);
              const code = v?.aniyor_id ? ` · ${v.aniyor_id}` : '';
              const isSelected = value === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => { onChange(v.id); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-50 text-blue-900 font-medium' : ''}`}
                  data-testid={`vendor-option-${v.aniyor_id || v.id}`}
                >
                  <div className="truncate">{name}{code}</div>
                  {v?.name && v?.name !== name && (
                    <div className="text-xs text-gray-500 truncate">{v.name}{v.email ? ` · ${v.email}` : ''}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


const InventoryNew = () => {
  const { user, apiCall } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [editedItems, setEditedItems] = useState({});
  const [editedCombinations, setEditedCombinations] = useState({}); // Track edited variant combinations
  const [expandedItems, setExpandedItems] = useState(new Set()); // Track expanded rows
  const [isSaving, setIsSaving] = useState(false);

  // Admin-only: vendor filter
  const [vendorFilter, setVendorFilter] = useState('all'); // 'all' | vendor.id
  const [vendors, setVendors] = useState([]);

  // Concurrency limit for bulk inventory PATCHes (avoids Shopify rate-limit)
  const runWithConcurrency = async (tasks, limit = 5) => {
    const results = [];
    let i = 0;
    const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
      while (i < tasks.length) {
        const idx = i++;
        try {
          results[idx] = { status: 'fulfilled', value: await tasks[idx]() };
        } catch (err) {
          results[idx] = { status: 'rejected', reason: err };
        }
      }
    });
    await Promise.all(workers);
    return results;
  };

  // Fetch vendors list (admin only) — uses canonical /api/vendors endpoint
  // (post iter-201) with multi-field search baked in. Resolves store name
  // through the same waterfall used everywhere else for consistency.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const data = await apiCall('/api/vendors/?status=active&limit=500');
        const list = Array.isArray(data) ? data : (data?.vendors || data?.data || []);
        // Sort by store_name for predictable scanability in the dropdown
        list.sort((a, b) => {
          const an = (a?.identity?.store_name || a?.vendor_profile?.business_details?.business_name || a?.name || '').toLowerCase();
          const bn = (b?.identity?.store_name || b?.vendor_profile?.business_details?.business_name || b?.name || '').toLowerCase();
          return an.localeCompare(bn);
        });
        setVendors(list);
      } catch (err) {
        console.warn('Failed to load vendors list for inventory filter', err);
      }
    })();
  }, [isAdmin, apiCall]);

  useEffect(() => {
    fetchInventory();
  }, [vendorFilter]);

  useEffect(() => {
    filterInventory();
  }, [searchTerm, inventory]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      // Admin can scope to a single vendor; vendors always see only their own listings (backend enforced)
      const url = isAdmin && vendorFilter && vendorFilter !== 'all'
        ? `/api/listings/?vendor_id=${vendorFilter}`
        : '/api/listings/';
      const response = await apiCall(url);
      
      // Transform listings into inventory items.
      // iter-204: Include subscription listings. For subscription listings
      // without variants, the "Available Capacity (Units per Day)" field
      // (data.inventory_available) is the inventory source — matches what
      // vendors actually fill in on the subscription form.
      // Services continue to be excluded — they have no inventory concept
      // (capacity-limited per slot; handled by booking system, not here).
      const INVENTORY_TYPES = ['product', 'products', 'rental', 'rentals', 'event', 'events', 'subscription', 'subscriptions'];
      const inventoryData = response
        .filter(item => INVENTORY_TYPES.includes((item.listing_type || '').toLowerCase()))
        .map(item => {
          const typeLower = (item.listing_type || '').toLowerCase();
          const isSubscription = typeLower === 'subscription' || typeLower === 'subscriptions';

          // Get variant combinations from multiple possible sources
          let variantCombinations = item.variantCombinations || item.variant_combinations || item.data?.variantCombinations || [];

          // Calculate inventory from variant combinations or use direct inventory
          let totalStock = 0;

          if (variantCombinations.length > 0) {
            totalStock = variantCombinations.reduce((sum, c) => sum + (c.stock || c.inventory || 0), 0);
          } else if (isSubscription) {
            // Subscriptions without variants: capacity field is the inventory source
            totalStock = item.data?.inventory_available ||
                        item.data?.daily_capacity ||
                        item.data?.capacity_per_day ||
                        item.data?.capacity ||
                        0;
          } else {
            totalStock = item.data?.inventory ||
                        item.data?.stock ||
                        item.data?.stock_quantity ||
                        item.data?.available_quantity ||
                        item.data?.ticketQuantity ||  // For events
                        item.data?.capacity ||  // For events
                        0;
          }

          return {
            id: item.id,
            name: item.title,
            sku: item.data?.sku || item.sku || '',
            image: item.media?.[0] || null,
            unavailable: item.data?.unavailable_quantity || 0,
            committed: item.data?.committed_quantity || 0,
            available: totalStock,
            onHand: totalStock,
            variants: item.data?.variants || item.variants || [],
            variantCombinations: variantCombinations,
            hasVariants: variantCombinations.length > 0,
            listingType: item.listing_type,
            isSubscription,
            capacityField: isSubscription && variantCombinations.length === 0 ? 'inventory_available' : null,
            vendor_id: item.vendor_id,
            vendor_name: item.vendor_name || item.vendor_store_name || item.data?.vendor_name || '',
            vendor_store_name: item.vendor_store_name || item.data?.vendor_store_name || '',
            shopifySynced: !!(item.shopify_product_id || Object.values(item.shopify_stores || {}).some(s => s?.product_id)),
            shopifySyncStatus: item.shopify_sync_status || 'unknown',
          };
        });
      
      setInventory(inventoryData);
      setFilteredInventory(inventoryData);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filterInventory = () => {
    if (!searchTerm) {
      setFilteredInventory(inventory);
      return;
    }
    
    const q = searchTerm.toLowerCase();
    const filtered = inventory.filter(item =>
      (item.name || '').toLowerCase().includes(q) ||
      (item.sku || '').toLowerCase().includes(q) ||
      (item.vendor_name || '').toLowerCase().includes(q) ||
      (item.vendor_store_name || '').toLowerCase().includes(q) ||
      (item.listingType || '').toLowerCase().includes(q)
    );
    setFilteredInventory(filtered);
  };

  // Toggle expanded state for items with variants
  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Handle editing of variant combination stock
  const handleCombinationStockChange = (listingId, combinationKey, newStock) => {
    const key = `${listingId}:${combinationKey}`;
    setEditedCombinations(prev => ({
      ...prev,
      [key]: parseInt(newStock) || 0
    }));
    
    // Also update local state for display
    setFilteredInventory(prev => prev.map(item => {
      if (item.id !== listingId) return item;
      
      const updatedCombos = item.variantCombinations.map(combo => {
        if (combo.combination_key !== combinationKey) return combo;
        return { ...combo, stock: parseInt(newStock) || 0 };
      });
      
      const newTotal = updatedCombos.reduce((sum, c) => sum + (c.stock || 0), 0);
      
      return {
        ...item,
        variantCombinations: updatedCombos,
        available: newTotal,
        onHand: newTotal
      };
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(new Set(filteredInventory.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId, checked) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleFieldChange = (itemId, field, value) => {
    const numValue = parseInt(value) || 0;
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: numValue
      }
    }));

    // Update the display immediately
    setFilteredInventory(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: numValue };
        // Recalculate onHand if needed
        if (field === 'available' || field === 'committed' || field === 'unavailable') {
          updated.onHand = (updated.available || 0) + (updated.committed || 0) + (updated.unavailable || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const handleSaveChanges = async () => {
    const hasBasicChanges = Object.keys(editedItems).length > 0;
    const hasComboChanges = Object.keys(editedCombinations).length > 0;
    
    if (!hasBasicChanges && !hasComboChanges) {
      toast.info('No changes to save');
      return;
    }

    try {
      setIsSaving(true);
      const updateTasks = [];  // array of () => Promise — run with concurrency limit
      
      // Save basic inventory changes (non-variant items)
      if (hasBasicChanges) {
        Object.entries(editedItems).forEach(([itemId, changes]) => {
          const item = inventory.find(i => i.id === itemId);
          const newInventory = changes.available !== undefined 
            ? changes.available 
            : (changes.onHand !== undefined ? changes.onHand : item.available);
          
          updateTasks.push(() =>
            apiCall(`/api/listings/${itemId}/inventory`, {
              method: 'PATCH',
              body: JSON.stringify({ inventory: newInventory })
            })
          );
        });
      }
      
      // Save variant combination changes - group by listing ID
      if (hasComboChanges) {
        const listingCombinations = {};
        
        Object.entries(editedCombinations).forEach(([key, _newStock]) => {
          const [listingId] = key.split(':');
          if (!listingCombinations[listingId]) {
            // Get all combinations for this listing from current state
            const item = filteredInventory.find(i => i.id === listingId);
            listingCombinations[listingId] = item?.variantCombinations || [];
          }
        });
        
        // For each listing with edited combinations, save the full combination array
        Object.entries(listingCombinations).forEach(([listingId, combinations]) => {
          updateTasks.push(() =>
            apiCall(`/api/listings/${listingId}/variant-inventory`, {
              method: 'PATCH',
              body: JSON.stringify({ variant_combinations: combinations })
            })
          );
        });
      }

      // Run with concurrency limit (5 in flight) — protects Shopify rate-limit on bulk admin saves
      const results = await runWithConcurrency(updateTasks, 5);

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failedCount = results.filter(r => r.status === 'rejected').length;

      // iter-204: surface Shopify sync status so vendors/admins know whether
      // the Shopify store actually reflects the inventory change. Previously
      // we only checked HTTP success, so silent sync failures (Shopify down,
      // rate-limit, mismatched variants) looked like success to the user —
      // resulting in "vendor updated inventory but Shopify shows out-of-stock"
      // complaints. Now we count three outcomes:
      //   • shopify_synced=true  → inventory pushed live to Shopify
      //   • shopify_skipped=true → listing not synced to Shopify (by design)
      //   • anything else        → queued retry (backend auto-retries via
      //                           iter-202 queue; admin can see in Shopify Sync)
      let shopifySyncedCount = 0;
      let shopifySkippedCount = 0;
      let shopifyRetryCount = 0;
      results.forEach(r => {
        if (r.status !== 'fulfilled') return;
        const resp = r.value || {};
        if (resp.shopify_synced) shopifySyncedCount++;
        else if (resp.shopify_skipped) shopifySkippedCount++;
        else shopifyRetryCount++;
      });

      if (successCount > 0) {
        let msg = `${successCount} item(s) updated`;
        if (shopifySyncedCount > 0) msg += ` · ${shopifySyncedCount} synced to Shopify`;
        if (shopifyRetryCount > 0) msg += ` · ${shopifyRetryCount} queued for retry`;
        if (shopifySkippedCount > 0 && shopifySyncedCount === 0 && shopifyRetryCount === 0) {
          msg += ` (not synced to Shopify — publish listing first)`;
        }
        toast.success(msg);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} item(s) failed to update`);
      }
      if (shopifyRetryCount > 0) {
        toast.warning(`${shopifyRetryCount} Shopify push(s) failed — retrying automatically`);
      }
      
      setEditedItems({});
      setEditedCombinations({});
      // Refresh the inventory list
      fetchInventory();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save some changes');
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = Object.keys(editedItems).length > 0 || Object.keys(editedCombinations).length > 0;
  const totalChanges = Object.keys(editedItems).length + Object.keys(editedCombinations).length;
  const [isSyncingShopify, setIsSyncingShopify] = useState(false);

  // Manual Shopify inventory sync for selected items
  const handleSyncSelectedToShopify = async () => {
    const syncableIds = [...selectedItems].filter(id => {
      const item = inventory.find(i => i.id === id);
      return item?.shopifySynced;
    });

    if (syncableIds.length === 0) {
      toast.error('No selected items are synced to Shopify');
      return;
    }

    setIsSyncingShopify(true);
    let success = 0;
    let failed = 0;

    try {
      const results = await Promise.allSettled(
        syncableIds.map(id =>
          apiCall(`/api/shopify-sync/sync-inventory/${id}`, { method: 'POST' })
        )
      );

      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value?.success) success++;
        else failed++;
      });

      if (success > 0) toast.success(`${success} item(s) synced to Shopify`);
      if (failed > 0) toast.error(`${failed} item(s) failed to sync`);
    } catch (error) {
      toast.error('Shopify sync failed');
    } finally {
      setIsSyncingShopify(false);
    }
  };

  const handleExport = () => {
    // Convert inventory to CSV
    const headers = ['SKU', 'Product Name', 'On Hand', 'Available', 'Committed', 'Unavailable'];
    const csvContent = [
      headers.join(','),
      ...filteredInventory.map(item => [
        item.sku,
        `"${item.name}"`,
        item.onHand,
        item.available,
        item.committed,
        item.unavailable
      ].join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Inventory exported successfully');
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const csv = event.target.result;
          const lines = csv.split('\n');
          const headers = lines[0].split(',');
          
          // Parse CSV
          const updates = [];
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            
            const values = lines[i].split(',');
            const sku = values[0]?.trim();
            const onHand = parseInt(values[2]) || 0;
            const available = parseInt(values[3]) || 0;
            const committed = parseInt(values[4]) || 0;
            const unavailable = parseInt(values[5]) || 0;
            
            // Find matching inventory item
            const item = inventory.find(inv => inv.sku === sku);
            if (item) {
              updates.push({
                id: item.id,
                data: {
                  stock_quantity: onHand,
                  available_quantity: available,
                  committed_quantity: committed,
                  unavailable_quantity: unavailable
                }
              });
            }
          }

          if (updates.length === 0) {
            toast.error('No matching products found in CSV');
            return;
          }

          // Update items
          const updatePromises = updates.map(update =>
            apiCall(`/api/listings/${update.id}`, {
              method: 'PUT',
              body: JSON.stringify(update.data)
            })
          );

          await Promise.all(updatePromises);
          
          toast.success(`${updates.length} items imported successfully`);
          fetchInventory();
        } catch (error) {
          console.error('Error importing:', error);
          toast.error('Failed to import inventory');
        }
      };
      
      reader.readAsText(file);
    };
    
    input.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">
              {isAdmin
                ? (vendorFilter === 'all'
                    ? 'Inventory — All Vendors'
                    : `Inventory — ${(vendors.find(v => v.id === vendorFilter)?.name) || 'Vendor'}`)
                : 'Inventory'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isAdmin
                ? 'Admin view — manage stock across all vendors. Updates apply on behalf of the vendor.'
                : 'Manage stock levels and track availability'}
            </p>
          </div>
          <div className="flex gap-2">
            {selectedItems.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncSelectedToShopify}
                disabled={isSyncingShopify}
                data-testid="sync-shopify-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isSyncingShopify ? 'animate-spin' : ''}`} />
                {isSyncingShopify ? 'Syncing...' : `Sync to Shopify (${selectedItems.size})`}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={handleImport}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            {hasUnsavedChanges && (
              <Button onClick={handleSaveChanges} disabled={isSaving} size="sm">
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : `Save Changes (${totalChanges})`}
              </Button>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={isAdmin ? "Search products, SKU, or vendor..." : "Search products..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="inventory-search-input"
                />
              </div>
              {isAdmin && (
                <div className="w-80">
                  {/* iter-204 searchable vendor picker — typing searches through
                      store name, aniyor code, personal name, or email; keeps the
                      "All Vendors" option as the header of the dropdown. */}
                  <VendorPicker
                    vendors={vendors}
                    value={vendorFilter}
                    onChange={setVendorFilter}
                  />
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
                <span className="text-sm font-medium">All</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Table */}
        {loading ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-gray-500">Loading inventory...</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left w-12">
                      <Checkbox
                        checked={selectedItems.size === filteredInventory.length && filteredInventory.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    {isAdmin && vendorFilter === 'all' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">Unavailable</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">Committed</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">Available</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">On hand</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInventory.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin && vendorFilter === 'all' ? 8 : 7} className="px-4 py-12 text-center">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No inventory items found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredInventory.map((item) => {
                      const isEdited = !!editedItems[item.id];
                      const isExpanded = expandedItems.has(item.id);
                      const hasComboEdits = Object.keys(editedCombinations).some(k => k.startsWith(`${item.id}:`));
                      
                      return (
                        <React.Fragment key={item.id}>
                          <tr className={`hover:bg-gray-50 ${isEdited || hasComboEdits ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {item.hasVariants && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => toggleExpanded(item.id)}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                                <Checkbox
                                  checked={selectedItems.has(item.id)}
                                  onCheckedChange={(checked) => handleSelectItem(item.id, checked)}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {item.image ? (
                                  <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded" />
                                ) : (
                                  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                    <Package className="h-5 w-5 text-gray-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-sm">{item.name}</p>
                                  {item.hasVariants && (
                                    <button 
                                      className="text-xs text-blue-600 hover:underline"
                                      onClick={() => toggleExpanded(item.id)}
                                    >
                                      {item.variantCombinations?.length || 0} variant SKUs {isExpanded ? '▲' : '▼'}
                                    </button>
                                  )}
                                  {item.variants && item.variants.length > 0 && !item.hasVariants && (
                                    <p className="text-xs text-gray-500">
                                      {item.variants.map(v => v.name).join(', ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            {isAdmin && vendorFilter === 'all' && (
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {item.vendor_name || <span className="text-gray-400">—</span>}
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.hasVariants ? (
                                <Badge variant="outline" className="text-xs">Multiple SKUs</Badge>
                              ) : (
                                item.sku || <span className="text-gray-400">No SKU</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Input
                                type="number"
                                value={item.unavailable}
                                onChange={(e) => handleFieldChange(item.id, 'unavailable', e.target.value)}
                                className="w-20 text-center"
                                min="0"
                                disabled={item.hasVariants}
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Input
                                type="number"
                                value={item.committed}
                                onChange={(e) => handleFieldChange(item.id, 'committed', e.target.value)}
                                className="w-20 text-center"
                                min="0"
                                disabled={item.hasVariants}
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.hasVariants ? (
                                <Badge variant="secondary" className="text-sm">{item.available}</Badge>
                              ) : (
                                <Input
                                  type="number"
                                  value={item.available}
                                  onChange={(e) => handleFieldChange(item.id, 'available', e.target.value)}
                                  className="w-20 text-center"
                                  min="0"
                                />
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.hasVariants ? (
                                <Badge className="text-sm font-semibold">{item.onHand}</Badge>
                              ) : (
                                <Input
                                  type="number"
                                  value={item.onHand}
                                  onChange={(e) => handleFieldChange(item.id, 'onHand', e.target.value)}
                                  className="w-20 text-center font-semibold"
                                  min="0"
                                />
                              )}
                            </td>
                          </tr>
                          
                          {/* Expanded variant combinations */}
                          {isExpanded && item.hasVariants && item.variantCombinations.map((combo, idx) => (
                            <tr key={`${item.id}-combo-${idx}`} className="bg-gray-50/50">
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 pl-16">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                                    {idx + 1}
                                  </div>
                                  <span className="text-sm text-gray-700">
                                    {combo.combination_key?.replace(/\|/g, ' / ') || `Combo ${idx + 1}`}
                                  </span>
                                </div>
                              </td>
                              {isAdmin && vendorFilter === 'all' && (
                                <td className="px-4 py-2"></td>
                              )}
                              <td className="px-4 py-2 text-sm text-gray-500">
                                {combo.sku || '-'}
                              </td>
                              <td className="px-4 py-2 text-center text-sm text-gray-400">-</td>
                              <td className="px-4 py-2 text-center text-sm text-gray-400">-</td>
                              <td className="px-4 py-2 text-center">
                                <span className="text-sm text-gray-600">{combo.stock || combo.inventory || 0}</span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <Input
                                  type="number"
                                  value={combo.stock || combo.inventory || 0}
                                  onChange={(e) => handleCombinationStockChange(item.id, combo.combination_key, e.target.value)}
                                  className="w-20 text-center text-sm"
                                  min="0"
                                />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredInventory.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled>
                    &lt;
                  </Button>
                  <span className="text-sm text-gray-600">1-{Math.min(50, filteredInventory.length)}</span>
                  <Button variant="outline" size="sm" disabled={filteredInventory.length <= 50}>
                    &gt;
                  </Button>
                </div>
                {hasUnsavedChanges && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {Object.keys(editedItems).length} unsaved change(s)
                  </Badge>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InventoryNew;
